import { useCallback, useEffect } from 'react';
import { PrimitiveAtom, useAtomValue, useSetAtom } from 'jotai';
import { VisibilityBoxProps, VisibilityBoxMode } from '@fabric-web/3d-viewer';
import queryString from 'query-string';
import { updateUrl } from '@/utils';
import { encodeNumber3, parseNumber3, sanitiseScale, restoreScale } from './utils';

export type VisibilityBoxDeeplinkState = {
  shouldUpdateUrl: boolean;
  currentVisibilityBoxAtom: PrimitiveAtom<VisibilityBoxProps>;
};

export const useVisibilityBoxDeeplink = (state: VisibilityBoxDeeplinkState) => {
  const { shouldUpdateUrl, currentVisibilityBoxAtom } = state;

  const currentVisibilityBox = useAtomValue(currentVisibilityBoxAtom);
  const setCurrentVisibilityBox = useSetAtom(currentVisibilityBoxAtom);

  useEffect(() => {
    if (!shouldUpdateUrl) return;
    const enabled = currentVisibilityBox.mode !== VisibilityBoxMode.Disabled;
    updateUrl('vbox.position', enabled ? encodeNumber3(currentVisibilityBox.position) : undefined);
    updateUrl('vbox.rotation', enabled ? encodeNumber3(currentVisibilityBox.rotation) : undefined);
    updateUrl(
      'vbox.scale',
      enabled ? encodeNumber3(currentVisibilityBox.scale, sanitiseScale) : undefined
    );
  }, [shouldUpdateUrl, currentVisibilityBox]);

  const applyFromParsed = useCallback(
    (parsed: queryString.ParsedQuery<string>) => {
      const vboxPosition = parseNumber3(parsed['vbox.position']);
      const vboxRotation = parseNumber3(parsed['vbox.rotation']);
      const vboxScale = parseNumber3(parsed['vbox.scale'], restoreScale);
      if (vboxPosition && vboxRotation && vboxScale) {
        setCurrentVisibilityBox((current) => ({
          ...current,
          mode: VisibilityBoxMode.Enabled,
          position: vboxPosition,
          rotation: vboxRotation,
          scale: vboxScale,
        }));
      }
    },
    [setCurrentVisibilityBox]
  );

  return { applyFromParsed };
};
